/**
 * Animated search placeholder.
 *
 * Types the field's placeholder out letter by letter and shows a thin blinking
 * caret (a positioned overlay that mimics the native text caret) at the end of
 * the text. The moment the visitor interacts with the field, the element hands
 * control back to the theme's native field (floating label + native caret).
 * Honours prefers-reduced-motion.
 *
 * Usage:
 *   <search-placeholder data-text="Search…">
 *     <div class="field">
 *       <input class="search__input" ...>
 *       <label ...>
 *     </div>
 *   </search-placeholder>
 */
// The header renders this snippet twice (mobile + desktop instance), so the
// whole definition is guarded to avoid re-declaring the class on second load.
if (!customElements.get('search-placeholder')) {
  class SearchPlaceholder extends HTMLElement {
    static TYPE_SPEED = 25; // ms per character
    static BLINK_SPEED = 500; // ms per caret blink
    static FADE = 200; // ms, must match the CSS transition

    #input = null;
    #field = null;
    #caret = null;
    #text = '';
    #charIndex = 0;
    #caretOn = true;
    #typeTimer = null;
    #blinkTimer = null;
    #clearTimer = null;
    #reduceMotion = false;
    #measureCtx = null;

    connectedCallback() {
      this.#input = this.querySelector('input');
      this.#field = this.querySelector('.field') || (this.#input && this.#input.closest('.field'));
      this.#text = this.dataset.text || '';
      if (!this.#input || !this.#field || !this.#text) return;

      this.#reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // The modal auto-focuses the field on open, so focus alone must NOT clear the
      // animation. Hand back to the native state only on a real interaction.
      this.#input.addEventListener('pointerdown', this.#restore);
      this.#input.addEventListener('keydown', this.#restore);
      this.#input.addEventListener('input', this.#restore);
      this.#input.addEventListener('blur', this.#onBlur);

      // Animate only while the field's own modal is open.
      const details = this.closest('details');
      if (details) {
        details.addEventListener('toggle', () => (details.open ? this.#present() : this.#stop()));
        if (details.open) this.#present();
      } else {
        this.#present();
      }
    }

    disconnectedCallback() {
      this.#stop();
      this.#caret?.remove();
      this.#caret = null;
    }

    #hasInput() {
      return this.#input.value.length > 0;
    }

    #stop() {
      clearTimeout(this.#typeTimer);
      clearInterval(this.#blinkTimer);
      clearTimeout(this.#clearTimer);
      this.#typeTimer = this.#blinkTimer = this.#clearTimer = null;
    }

    // Lazily create the caret overlay inside the (position:relative) field.
    #ensureCaret() {
      if (this.#caret) return this.#caret;
      const caret = document.createElement('span');
      caret.className = 'search-placeholder__caret';
      caret.setAttribute('aria-hidden', 'true');
      this.#field.appendChild(caret);
      this.#caret = caret;
      return caret;
    }

    // Measure the placeholder text width in the input's own font so the caret can
    // sit exactly at the end of the typed characters.
    #textWidth(text) {
      if (!this.#measureCtx) {
        this.#measureCtx = document.createElement('canvas').getContext('2d');
      }
      const cs = getComputedStyle(this.#input);
      this.#measureCtx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      return this.#measureCtx.measureText(text).width;
    }

    // Place the caret just after the currently visible text.
    #positionCaret(visibleText) {
      const caret = this.#ensureCaret();
      const cs = getComputedStyle(this.#input);
      const left = parseFloat(cs.paddingLeft) + this.#textWidth(visibleText) + 1;
      caret.style.left = `${left}px`;
      caret.style.fontSize = cs.fontSize; // 1.1em caret height tracks the text size
    }

    #showCaret(on) {
      this.#ensureCaret().classList.toggle('search-placeholder__caret--on', on);
    }

    #present = () => {
      this.#stop();
      if (this.#hasInput()) {
        this.#restore();
        return;
      }
      this.#input.classList.add('is-animating');
      if (this.#reduceMotion) {
        this.#input.setAttribute('placeholder', this.#text);
        return;
      }
      this.#charIndex = 0;
      this.#caretOn = true;
      this.#input.setAttribute('placeholder', '');
      this.#positionCaret('');
      this.#showCaret(true);
      this.#typeTimer = setTimeout(this.#typeNext, SearchPlaceholder.TYPE_SPEED);
    };

    #typeNext = () => {
      if (this.#hasInput()) return;
      this.#charIndex++;
      const visible = this.#text.slice(0, this.#charIndex);
      this.#input.setAttribute('placeholder', visible);
      this.#positionCaret(visible);
      this.#showCaret(true);
      if (this.#charIndex < this.#text.length) {
        this.#typeTimer = setTimeout(this.#typeNext, SearchPlaceholder.TYPE_SPEED);
      } else {
        this.#blinkTimer = setInterval(this.#blink, SearchPlaceholder.BLINK_SPEED);
      }
    };

    #blink = () => {
      if (this.#hasInput() || document.hidden) return; // pause when the tab is backgrounded
      this.#caretOn = !this.#caretOn;
      this.#showCaret(this.#caretOn);
    };

    // Hand the field back to the theme's native behaviour: floating label + native
    // caret. Removing the class fades the placeholder out over the CSS transition;
    // the text is cleared only once that fade has finished so the words don't pop.
    #restore = () => {
      this.#stop();
      this.#showCaret(false);
      this.#input.classList.remove('is-animating');
      if (this.#reduceMotion) {
        this.#input.setAttribute('placeholder', '');
        return;
      }
      this.#clearTimer = setTimeout(() => {
        if (!this.#input.classList.contains('is-animating')) {
          this.#input.setAttribute('placeholder', '');
        }
      }, SearchPlaceholder.FADE);
    };

    // Leaving an empty field brings the placeholder back.
    #onBlur = () => {
      if (!this.#hasInput()) this.#present();
    };
  }
  customElements.define('search-placeholder', SearchPlaceholder);
}
